import React, { Fragment, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import LocaleLink from '../../components/LocaleLink';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  PageContainer,
  Container,
  Section,
  PageTitle,
  PageSubtitle,
  SolutionsHeroSection,
  HeroContent,
  HeroTitle,
  HeroContentWrapper,
  ContentText,
  ContentSection,
  BulletList,
  InfoTitle,
  IndustryCardLink,
  IndustryCardImage,
  IndustryCardContent,
  IndustryCardTitle,
  IndustryCardDescription,
  PartnershipSection,
  PartnershipContent,
  SolutionsCtaTitle,
  SolutionsCtaText,
  SolutionsCtaButton,
  SubsectionsGrid,
  SubsectionLink,
  CtaImageSection,
  LazyBackgroundImage,
  LazyImage,
  staggerContainer,
  staggerItem,
  heroStaggerContainerVariants,
  heroStaggerItemVariants,
  CardArrowIcon,
  CardArrowButton,
  GridContainer,
  SolutionsSectionHeading,
  ItemCardLight,
  ItemBody,
  ImageTextSection,
  ImageTextImage,
  ImageTextContent,
  SectionDivider,
} from '../../components/shared/SharedComponents';
import { useContent } from '../../hooks/useContent';
import { useLocale, LOCALES } from '../../context/LocaleContext';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import SEO from '../../components/SEO';
import {
  preloadCriticalImages,
  preloadImagesInBackground,
  preloadImagesForSolutionsPath,
  getSubsectionImageUrls,
} from '../../utils/imagePreloader';
import PreloadZone from '../../components/PreloadZone';
import { BACKGROUND_PRELOAD_MAX_SUBSECTIONS } from '../../config/preloadConfig';

const CARD_GRID_SECTION_TITLE = {
  [LOCALES.EN]: 'Core Strategic Supply Domains',
  [LOCALES.ZH_CN]: '核心战略供应领域',
};

const MotionItemCard = motion(ItemCardLight);

const InlineButtonWrapper = styled.div`
  margin: 0;
`;

const InlineButton = styled(LocaleLink)`
  display: inline-block;
  background: linear-gradient(135deg, #BF1922 0%, #8B1419 100%);
  color: #ffffff;
  padding: 14px 28px;
  border-radius: 50px;
  font-weight: 500;
  font-size: 16px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(191, 25, 34, 0.35);
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
  margin-top: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ThreeBulletCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FourBulletCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const TwoInfoCardImageWrap = styled.div`
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 20px;
  aspect-ratio: 16 / 10;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ThreeBulletSectionImage = styled(motion.div)`
  border-radius: 24px;
  overflow: hidden;
  margin-top: 8px;
  margin-bottom: 28px;
  aspect-ratio: 21 / 9;
  max-height: 420px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 768px) {
    aspect-ratio: 16 / 10;
    max-height: none;
  }
`;

/* Stacked card image: full container width, no max-height cap (e.g. Our Role in Marine) */
const StackedFullWidthSectionImage = styled(motion.div)`
  border-radius: 24px;
  overflow: hidden;
  margin: 0 0 28px;
  width: 100%;
  aspect-ratio: 21 / 9;
  min-height: 280px;
  max-height: none;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 768px) {
    min-height: 200px;
    aspect-ratio: 16 / 10;
  }
`;

/* Full-width heading above image|text grid — same typography as InfoTitle (e.g. threeBulletCards section titles) */
const ImageTextOuterSectionTitle = styled(InfoTitle)`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

/** Full-width title row, then a separate two-column image|text grid (e.g. Aerospace imageText cards) */
const ImageTextCardVerticalStack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: stretch;
`;

/**
 * Optional `titleAlign` on twoInfoCards card objects: "left" | "right" | "center" (content.json).
 */
const SectionCardTitleRow = styled.div`
  width: 100%;
  text-align: ${({ $align }) => ($align === 'right' || $align === 'center' ? $align : 'left')};
`;

const ImageBesideBulletsIntro = styled.div`
  margin-bottom: 16px;
  font-size: 16px;
  color: rgba(29, 51, 78, 0.75);
  line-height: 1.6;

  p {
    margin: 0;
  }
`;

const ImageBesideBulletsHeader = styled.div`
  text-align: left;
  max-width: none;
  margin: 0 0 28px;
`;

const ImageBesideBulletsColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: stretch;
  margin: 0 0 40px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const ImageBesideBulletsImageCol = styled.div`
  min-height: 280px;
  display: flex;
  min-width: 0;

  @media (max-width: 968px) {
    min-height: 220px;
    order: -1;
  }
`;

const ImageBesideBulletsImageBox = styled.div`
  flex: 1;
  width: 100%;
  min-height: 280px;
  border-radius: 24px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);

  > div {
    height: 100%;
    min-height: inherit;
  }

  @media (max-width: 968px) {
    min-height: 220px;
  }
`;

const ImageBesideBulletsListCol = styled.div`
  margin: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SolutionsImageTextSection = styled(ImageTextSection)`
  margin-top: 0;
  margin-bottom: 0;
`;

const ImageClipWrapper = styled.div`
  overflow: hidden;
  min-height: 200px;

  @media (max-width: 768px) {
    min-height: 0;
    aspect-ratio: 16 / 10;
  }
`;

const SectionPageCardImage = styled(IndustryCardImage)`
  transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const CtaSection = styled(Section)`
  padding-top: 0;
`;

const SectionPageCard = styled(IndustryCardLink)`
  min-height: 180px;
  height: 100%;
  grid-template-columns: ${({ $hasImage }) => ($hasImage ? 'minmax(0, 0.8fr) minmax(0, 1.5fr)' : '1fr')};
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    box-shadow 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    min-height: auto;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.1);

    ${SectionPageCardImage} {
      transform: scale(1.04);
    }
  }
`;

const SectionPageCardTitle = styled(IndustryCardTitle)`
  font-size: 18px;
  line-height: 1.3;
  margin-bottom: 6px;
`;

const SectionPageCardDescription = styled(IndustryCardDescription)`
  font-size: 14px;
  line-height: 1.5;
`;

function getCardTitleAlign(card) {
  const raw =
    card?.titleAlign != null ? String(card.titleAlign).trim().toLowerCase() : '';
  if (raw === 'right' || raw === 'center' || raw === 'left') return raw;
  return 'left';
}

/** Optional `titleLines` on twoInfoCards (stacked sideBySideImageText): render as separate lines. */
function cardHasVisibleTitle(card) {
  const hasTitle = card?.title != null && String(card.title).trim();
  const lines = card?.titleLines;
  const hasLines = Array.isArray(lines) && lines.some((l) => l != null && String(l).trim());
  return Boolean(hasTitle || hasLines);
}

function cardTitleForDisplay(card) {
  const lines = card?.titleLines;
  if (Array.isArray(lines) && lines.length > 0) {
    return lines.map((line, i) => (
      <Fragment key={i}>
        {i > 0 ? <br /> : null}
        {line}
      </Fragment>
    ));
  }
  return card?.title != null ? card.title : null;
}

function cardTitleForAlt(card) {
  const lines = card?.titleLines;
  if (Array.isArray(lines) && lines.length > 0) {
    return lines.map((l) => String(l)).join(' ');
  }
  return card?.title != null ? String(card.title) : '';
}

const ViewLabelHero = styled.span`
  display: inline-flex;
  align-items: center;
  color: #BF1922;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-left: 8px;
  opacity: 0.95;
  transition: opacity 0.2s ease;

  svg {
    margin-left: 4px;
  }

  a:hover &,
  a:focus & {
    opacity: 1;
  }
`;

/** Subheading before linked list (e.g. linkListSection.listTitle) — excludes ContentText default ul dots */
const LinkListSubtitle = styled.h5`
  font-size: clamp(18px, 2.5vw, 21px);
  font-weight: 800;
  color: #1d334e;
  margin: 28px 0 12px 0;
  letter-spacing: -0.02em;

  &:first-child {
    margin-top: 0;
  }
`;

/** Arrows only (no brand dots); `data-list-variant` skips ContentText `ul` bullet injection */
const LinkListSectionList = styled.ul.attrs({ 'data-list-variant': 'arrow-links' })`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 10px;

  li {
    display: flex;
    align-items: center;
    /* Match ContentText paragraph sizing */
    font-size: clamp(16px, 2.5vw, 16px);
    font-weight: 400;
    color: rgba(29, 51, 78, 0.85);
    line-height: 1.5;
    position: static;
    padding-left: 0;
    margin-bottom: 0;
  }

  li::before {
    content: none !important;
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: inherit;
    font-weight: inherit;
  }

  a::before {
    content: '→';
    color: #bf1922;
    font-weight: 600;
    font-size: 14px;
    line-height: 1;
  }
`;

const LinkListSectionLink = styled(LocaleLink)`
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover,
  &:focus-visible {
    color: #bf1922;
  }
`;

const SectionPage = () => {
  useScrollToTop();
  const { locale } = useLocale();
  const { solutions } = useContent();
  const { sectionId } = useParams();

  const section = solutions?.sections?.[sectionId];

  // Preload critical images (hero + section content card images from cardGrid blocks or legacy shape)
  useEffect(() => {
    const criticalImages = [];
    if (section?.heroImage) criticalImages.push(section.heroImage);
    const sc = section?.sectionContent;
    if (sc?.blocks) {
      sc.blocks.forEach((block) => {
        if (block.type === 'cardGrid' && block.items) {
          block.items.forEach((item) => item.image && criticalImages.push(item.image));
        }
        if (block.type === 'twoInfoCards' && block.cards) {
          block.cards.forEach((card) => {
            if (card.image) criticalImages.push(card.image);
          });
        }
        if (block.type === 'threeBulletCards' && block.image) {
          criticalImages.push(block.image);
        }
      });
    } else if (sc) {
      const cardSource = sc.powerSystems || sc.coreAreas;
      cardSource?.items?.forEach((item) => item.image && criticalImages.push(item.image));
    }
    if (criticalImages.length > 0) preloadCriticalImages(criticalImages);
  }, [section?.heroImage, section?.sectionContent]);

  // Background preload: subsection pages' images so they load fast when user clicks a card
  useEffect(() => {
    if (!section?.subsections) return;
    const entries = Object.entries(section.subsections);
    const urls = [];
    entries.slice(0, BACKGROUND_PRELOAD_MAX_SUBSECTIONS).forEach(([, subsection]) => {
      urls.push(...getSubsectionImageUrls(subsection, section));
    });
    if (urls.length > 0) preloadImagesInBackground(urls);
  }, [section]);

  if (!section) {
    return (
      <PageContainer>
        <Container>
          <PageTitle
            className="with-underline"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            Section Not Found
          </PageTitle>
          <PageSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            The requested section could not be found.
          </PageSubtitle>
        </Container>
      </PageContainer>
    );
  }

  const subsections = section.subsections ? Object.entries(section.subsections) : [];
  const sc = section.sectionContent;

  // Use blocks array if present; otherwise build from legacy sectionContent shape so no content is lost
  const blocks = (() => {
    if (sc?.blocks?.length) return sc.blocks;
    if (!sc) return [];
    const legacy = [];
    if (sc.intro?.paragraphs?.length) {
      legacy.push({ type: 'intro', paragraphs: sc.intro.paragraphs });
    }
    if (sc.sectorChallenges) {
      const b = sc.sectorChallenges;
      legacy.push({ type: 'bulletBlock', title: b.title, intro: b.intro, bullets: b.bullets || [], closing: b.closing });
    }
    if (sc.ourRole) {
      const b = sc.ourRole;
      legacy.push({ type: 'bulletBlock', title: b.title, intro: b.intro, bullets: b.bullets || [], closing: b.closing });
    }
    if (sc.coreEquipmentFocus) {
      const b = sc.coreEquipmentFocus;
      legacy.push({ type: 'bulletBlock', title: b.title, intro: b.intro, bullets: b.bullets || [], closing: b.closing });
    }
    const cardSource = sc.powerSystems || sc.coreAreas;
    if (cardSource?.items?.length) {
      legacy.push({
        type: 'cardGrid',
        title: cardSource.title,
        intro: cardSource.intro,
        items: cardSource.items,
        closing: cardSource.closing
      });
    }
    if (sc.howWeWork) {
      const b = sc.howWeWork;
      legacy.push({ type: 'bulletBlock', title: b.title, intro: b.intro, bullets: b.bullets || [], closing: b.closing });
    }
    if (sc.designedFor) {
      const b = sc.designedFor;
      legacy.push({ type: 'bulletBlock', title: b.title, intro: b.intro, bullets: b.bullets || [], closing: b.closing });
    }
    if (sc.cta) {
      legacy.push({
        type: 'cta',
        title: sc.cta.title,
        text: sc.cta.text,
        buttonText: sc.cta.buttonText,
        buttonLink: sc.cta.buttonLink
      });
    }
    return legacy;
  })();

  const hasSectionContent = section.sectionContent != null && blocks.length > 0;

  const renderBlock = (block, index) => {
    if (block.type === 'divider') {
      return (
        <ContentSection key={index}>
          <SectionDivider />
        </ContentSection>
      );
    }
    if (block.type === 'intro') {
      const paragraphs = block.paragraphs ?? [];
      if (!paragraphs.length) return null;
      const heading = block.title ?? (index === 0 ? (section.subtitle || section.description) : null);
      return (
        <ContentSection key={index}>
          {heading != null && String(heading).trim() && (
            <InfoTitle as={motion.h3} variants={staggerItem}>{heading}</InfoTitle>
          )}
          <ContentText as={motion.div} variants={staggerItem}>
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </ContentText>
        </ContentSection>
      );
    }
    if (block.type === 'bulletBlock') {
      const hasBullets = (block.bullets ?? []).length > 0;
      const hasContent = block.title || block.intro || hasBullets || block.closing;
      if (!hasContent) return null;
      return (
        <ContentSection key={index}>
          {block.title != null && String(block.title).trim() && (
            <InfoTitle as={motion.h3} variants={staggerItem}>{block.title}</InfoTitle>
          )}
          <ContentText as={motion.div} variants={staggerItem}>
            {block.intro != null && String(block.intro).trim() && <p>{block.intro}</p>}
            {hasBullets && (
              <BulletList>
                {(block.bullets || []).map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </BulletList>
            )}
            {block.closing != null && String(block.closing).trim() && <p>{block.closing}</p>}
          </ContentText>
        </ContentSection>
      );
    }
    if (block.type === 'linkListSection') {
      const paragraphs = block.paragraphs ?? [];
      const items = block.items ?? [];
      const hasTitle = block.title != null && String(block.title).trim();
      const hasParas = paragraphs.length > 0;
      const listTitleText = block.listTitle != null && String(block.listTitle).trim()
        ? String(block.listTitle).trim()
        : '';
      const hasListContent = items.length > 0 || listTitleText;
      if (!hasTitle && !hasParas && !hasListContent) return null;
      return (
        <ContentSection key={index}>
          {hasTitle && (
            <InfoTitle as={motion.h3} variants={staggerItem}>{String(block.title).trim()}</InfoTitle>
          )}
          <ContentText as={motion.div} variants={staggerItem}>
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            {listTitleText && <LinkListSubtitle>{listTitleText}</LinkListSubtitle>}
            {items.length > 0 && (
              <LinkListSectionList>
                {items.map((item, i) => {
                  const to = item.linkTo != null && String(item.linkTo).trim()
                    ? String(item.linkTo).trim()
                    : '#';
                  const label = item.label != null ? String(item.label) : '';
                  return (
                    <li key={i}>
                      <LinkListSectionLink to={to}>{label}</LinkListSectionLink>
                    </li>
                  );
                })}
              </LinkListSectionList>
            )}
          </ContentText>
        </ContentSection>
      );
    }
    if (block.type === 'twoInfoCards') {
      const cards = block.cards ?? [];
      if (cards.length < 2) return null;
      const layoutMode = (block.layout != null && String(block.layout).trim().toLowerCase()) || '';
      const layoutStacked = layoutMode === 'stacked';
      const layoutImageText = layoutMode === 'imagetext' || layoutMode === 'image-text';

      const renderCardBody = (card) => (
        <>
          {card.title != null && String(card.title).trim() && (
            <InfoTitle as={motion.h3} variants={staggerItem}>{card.title}</InfoTitle>
          )}
          <ContentText as={motion.div} variants={staggerItem}>
            {card.intro != null && String(card.intro).trim() && <p>{card.intro}</p>}
            {(card.bullets ?? []).length > 0 && (
              <BulletList>
                {card.bullets.map((b, bi) => (
                  <li key={bi}>{b}</li>
                ))}
              </BulletList>
            )}
            {card.closing != null && String(card.closing).trim() && <p>{card.closing}</p>}
          </ContentText>
        </>
      );

      if (layoutImageText) {
        return (
          <Fragment key={index}>
            {cards.map((card, i) => {
              const imageOnRight =
                (card.imagePosition != null && String(card.imagePosition).trim().toLowerCase()) === 'right';
              const imageBlock = (
                <ImageTextImage as={motion.div} variants={staggerItem}>
                  {card.image != null && String(card.image).trim() && (
                    <LazyImage
                      src={String(card.image).trim()}
                      alt={card.title != null && String(card.title).trim() ? String(card.title) : ''}
                      height="100%"
                      objectFit="cover"
                      critical
                    />
                  )}
                </ImageTextImage>
              );
              const contentBlock = (
                <ImageTextContent as={motion.div} variants={staggerItem}>
                  {card.intro != null && String(card.intro).trim() && <p>{card.intro}</p>}
                  {(card.bullets ?? []).length > 0 && (
                    <BulletList>
                      {card.bullets.map((b, bi) => (
                        <li key={bi}>{b}</li>
                      ))}
                    </BulletList>
                  )}
                  {card.closing != null && String(card.closing).trim() && <p>{card.closing}</p>}
                </ImageTextContent>
              );
              const row = imageOnRight ? (
                <>
                  {contentBlock}
                  {imageBlock}
                </>
              ) : (
                <>
                  {imageBlock}
                  {contentBlock}
                </>
              );
              return (
                <Fragment key={`${index}-${i}`}>
                  <ContentSection>
                    <ImageTextCardVerticalStack>
                      {card.title != null && String(card.title).trim() && (
                        <SectionCardTitleRow $align={getCardTitleAlign(card)}>
                          <ImageTextOuterSectionTitle as={motion.h3} variants={staggerItem}>
                            {card.title}
                          </ImageTextOuterSectionTitle>
                        </SectionCardTitleRow>
                      )}
                      <SolutionsImageTextSection as={motion.div} variants={staggerContainer}>
                        {row}
                      </SolutionsImageTextSection>
                    </ImageTextCardVerticalStack>
                  </ContentSection>
                </Fragment>
              );
            })}
          </Fragment>
        );
      }

      if (layoutStacked) {
        return (
          <Fragment key={index}>
            {cards.map((card, i) => {
              const cardLayout = (card.cardLayout != null && String(card.cardLayout).trim().toLowerCase()) || '';
              const isTitleImageBullets = cardLayout === 'titleimagebullets';
              if (isTitleImageBullets) {
                const imgSrc = String(card.image || '').trim();
                return (
                  <Fragment key={`${index}-${i}`}>
                    <ContentSection>
                      {card.title != null && String(card.title).trim() && (
                        <SectionCardTitleRow $align={getCardTitleAlign(card)}>
                          <InfoTitle as={motion.h3} variants={staggerItem}>{card.title}</InfoTitle>
                        </SectionCardTitleRow>
                      )}
                      {imgSrc && (
                        <StackedFullWidthSectionImage as={motion.div} variants={staggerItem}>
                          <LazyImage
                            src={imgSrc}
                            alt={card.title != null && String(card.title).trim() ? String(card.title) : ''}
                            height="100%"
                            objectFit="cover"
                            critical
                          />
                        </StackedFullWidthSectionImage>
                      )}
                      <ContentText as={motion.div} variants={staggerItem}>
                        {card.intro != null && String(card.intro).trim() && <p>{card.intro}</p>}
                        {(card.bullets ?? []).length > 0 && (
                          <BulletList>
                            {card.bullets.map((b, bi) => (
                              <li key={bi}>{b}</li>
                            ))}
                          </BulletList>
                        )}
                        {card.closing != null && String(card.closing).trim() && <p>{card.closing}</p>}
                      </ContentText>
                    </ContentSection>
                  </Fragment>
                );
              }
              const isSideBySideImageText =
                cardLayout === 'bannerimagetext' ||
                cardLayout === 'sidebysideimagetext';
              if (isSideBySideImageText) {
                const imgSrc = String(card.image || '').trim();
                const imageOnRight =
                  (card.imagePosition != null && String(card.imagePosition).trim().toLowerCase()) === 'right';
                const imageBlock = (
                  <ImageTextImage as={motion.div} variants={staggerItem}>
                    {imgSrc && (
                      <LazyImage
                        src={imgSrc}
                        alt={cardTitleForAlt(card)}
                        height="100%"
                        objectFit="cover"
                        critical
                      />
                    )}
                  </ImageTextImage>
                );
                const contentBlock = (
                  <ImageTextContent as={motion.div} variants={staggerItem}>
                    {card.intro != null && String(card.intro).trim() && <p>{card.intro}</p>}
                    {(card.bullets ?? []).length > 0 && (
                      <BulletList>
                        {card.bullets.map((b, bi) => (
                          <li key={bi}>{b}</li>
                        ))}
                      </BulletList>
                    )}
                    {card.closing != null && String(card.closing).trim() && <p>{card.closing}</p>}
                  </ImageTextContent>
                );
                const row = imageOnRight ? (
                  <>
                    {contentBlock}
                    {imageBlock}
                  </>
                ) : (
                  <>
                    {imageBlock}
                    {contentBlock}
                  </>
                );
                return (
                  <Fragment key={`${index}-${i}`}>
                    <ContentSection>
                      {cardHasVisibleTitle(card) && (
                        <SectionCardTitleRow $align={getCardTitleAlign(card)}>
                          <InfoTitle as={motion.h3} variants={staggerItem}>
                            {cardTitleForDisplay(card)}
                          </InfoTitle>
                        </SectionCardTitleRow>
                      )}
                      <SolutionsImageTextSection as={motion.div} variants={staggerContainer}>
                        {row}
                      </SolutionsImageTextSection>
                    </ContentSection>
                  </Fragment>
                );
              }

              const fullWidthStacked =
                card.stackedImageFullWidth === true ||
                (card.imageVariant != null && String(card.imageVariant).trim().toLowerCase()) === 'fullwidth';
              const StackedImg = fullWidthStacked ? StackedFullWidthSectionImage : ThreeBulletSectionImage;
              return (
                <Fragment key={`${index}-${i}`}>
                  <ContentSection>
                    {card.image != null && String(card.image).trim() && (
                      <StackedImg as={motion.div} variants={staggerItem}>
                        <LazyImage
                          src={String(card.image).trim()}
                          alt={card.title != null && String(card.title).trim() ? String(card.title) : ''}
                          height="100%"
                          objectFit="cover"
                          critical
                        />
                      </StackedImg>
                    )}
                    {renderCardBody(card)}
                  </ContentSection>
                </Fragment>
              );
            })}
          </Fragment>
        );
      }

      return (
        <ContentSection key={index}>
          <GridContainer className="two-columns" as={motion.div} variants={staggerContainer}>
            {cards.map((card, i) => (
              <motion.div key={i} variants={staggerItem}>
                {card.image != null && String(card.image).trim() && (
                  <TwoInfoCardImageWrap as={motion.div} variants={staggerItem}>
                    <LazyImage
                      src={card.image}
                      alt={card.title != null && String(card.title).trim() ? String(card.title) : ''}
                      height="100%"
                      objectFit="cover"
                      critical
                    />
                  </TwoInfoCardImageWrap>
                )}
                {card.title != null && String(card.title).trim() && (
                  <SolutionsSectionHeading>{card.title}</SolutionsSectionHeading>
                )}
                <ContentText>
                  {card.intro != null && String(card.intro).trim() && <p>{card.intro}</p>}
                  {(card.bullets ?? []).length > 0 && (
                    <BulletList>
                      {card.bullets.map((b, bi) => (
                        <li key={bi}>{b}</li>
                      ))}
                    </BulletList>
                  )}
                  {card.closing != null && String(card.closing).trim() && <p>{card.closing}</p>}
                </ContentText>
              </motion.div>
            ))}
          </GridContainer>
        </ContentSection>
      );
    }
    if (block.type === 'threeBulletCards') {
      const bullets = block.bullets ?? [];
      const items = block.items ?? [];
      let cardTexts = [];
      if (items.length >= 3) {
        cardTexts = items.slice(0, 3);
      } else if (bullets.length === 3) {
        cardTexts = bullets;
      } else if (bullets.length === 4) {
        cardTexts = bullets;
      } else if (bullets.length > 4) {
        cardTexts = [bullets[0], bullets[1], [bullets[2], bullets[3]].filter(Boolean).join(' • ')];
      } else if (bullets.length > 0) {
        cardTexts = bullets.slice(0, 3);
      }
      const useFourCol = cardTexts.length === 4;
      const useThreeCol = cardTexts.length === 3;
      if (!useFourCol && !useThreeCol) return null;
      const Grid = useFourCol ? FourBulletCardsGrid : ThreeBulletCardsGrid;
      const blockImage = block.image != null && String(block.image).trim() ? String(block.image) : null;
      const blockTitle = block.title != null && String(block.title).trim() ? String(block.title) : '';
      const threeLayoutMode = (block.layout != null && String(block.layout).trim().toLowerCase()) || '';
      const imageBesideBullets =
        threeLayoutMode === 'imagebesidebullets' || threeLayoutMode === 'image-beside-bullets';

      if (imageBesideBullets && blockImage && useThreeCol) {
        return (
          <ContentSection key={index}>
            <ImageBesideBulletsHeader as={motion.div} variants={staggerItem}>
              {block.title != null && String(block.title).trim() && (
                <InfoTitle as={motion.h3}>{block.title}</InfoTitle>
              )}
            </ImageBesideBulletsHeader>
            <ImageBesideBulletsColumns as={motion.div} variants={staggerContainer}>
              <ImageBesideBulletsImageCol>
                <ImageBesideBulletsImageBox>
                  <LazyImage
                    src={blockImage}
                    alt={blockTitle}
                    height="100%"
                    objectFit="cover"
                    critical
                  />
                </ImageBesideBulletsImageBox>
              </ImageBesideBulletsImageCol>
              <ImageBesideBulletsListCol>
                {block.intro != null && String(block.intro).trim() && (
                  <ImageBesideBulletsIntro as={motion.div} variants={staggerItem}>
                    <p>{block.intro}</p>
                  </ImageBesideBulletsIntro>
                )}
                {cardTexts.map((text, i) => (
                  <MotionItemCard key={i} variants={staggerItem}>
                    <ItemBody>
                      <p>{text}</p>
                    </ItemBody>
                  </MotionItemCard>
                ))}
              </ImageBesideBulletsListCol>
            </ImageBesideBulletsColumns>
            {block.closing != null && String(block.closing).trim() && (
              <ContentText as={motion.div} variants={staggerItem} style={{ marginTop: 8 }}>
                <p>{block.closing}</p>
              </ContentText>
            )}
          </ContentSection>
        );
      }

      return (
        <ContentSection key={index}>
          {block.title != null && String(block.title).trim() && (
            <InfoTitle as={motion.h3} variants={staggerItem}>{block.title}</InfoTitle>
          )}
          <ContentText as={motion.div} variants={staggerItem}>
            {block.intro != null && String(block.intro).trim() && <p>{block.intro}</p>}
          </ContentText>
          {blockImage && (
            <ThreeBulletSectionImage as={motion.div} variants={staggerItem}>
              <LazyImage
                src={blockImage}
                alt={blockTitle}
                height="100%"
                objectFit="cover"
                critical
              />
            </ThreeBulletSectionImage>
          )}
          <Grid as={motion.div} variants={staggerContainer}>
            {cardTexts.map((text, i) => (
              <MotionItemCard key={i} variants={staggerItem}>
                <ItemBody>
                  <p>{text}</p>
                </ItemBody>
              </MotionItemCard>
            ))}
          </Grid>
          <ContentText as={motion.div} variants={staggerItem}>
            {block.closing != null && String(block.closing).trim() && <p>{block.closing}</p>}
          </ContentText>
        </ContentSection>
      );
    }
    if (block.type === 'image-text') {
      const imageSrc = block.image != null && String(block.image).trim() ? String(block.image).trim() : '';
      const hasText =
        (block.title != null && String(block.title).trim()) ||
        (block.intro != null && String(block.intro).trim()) ||
        (block.bullets ?? []).length > 0 ||
        (block.closing != null && String(block.closing).trim());
      if (!imageSrc && !hasText) return null;
      const imageOnRight =
        (block.imagePosition != null && String(block.imagePosition).trim().toLowerCase()) === 'right';
      const imageBlock = (
        <ImageTextImage as={motion.div} variants={staggerItem}>
          {imageSrc && (
            <LazyImage
              src={imageSrc}
              alt={block.title != null && String(block.title).trim() ? String(block.title).trim() : ''}
              height="100%"
              objectFit="cover"
              critical
            />
          )}
        </ImageTextImage>
      );
      const contentBlock = (
        <ImageTextContent as={motion.div} variants={staggerItem}>
          {block.title != null && String(block.title).trim() && (
            <InfoTitle as={motion.h3} variants={staggerItem}>{block.title}</InfoTitle>
          )}
          <ContentText as={motion.div} variants={staggerItem}>
            {block.intro != null && String(block.intro).trim() && <p>{block.intro}</p>}
            {(block.bullets ?? []).length > 0 && (
              <BulletList>
                {block.bullets.map((b, bi) => (
                  <li key={bi}>{b}</li>
                ))}
              </BulletList>
            )}
            {block.closing != null && String(block.closing).trim() && <p>{block.closing}</p>}
          </ContentText>
        </ImageTextContent>
      );
      return (
        <ContentSection key={index}>
          <SolutionsImageTextSection as={motion.div} variants={staggerContainer}>
            {imageOnRight ? (
              <>
                {contentBlock}
                {imageBlock}
              </>
            ) : (
              <>
                {imageBlock}
                {contentBlock}
              </>
            )}
          </SolutionsImageTextSection>
        </ContentSection>
      );
    }
    if (block.type === 'cardGrid') {
      const items = block.items ?? [];
      const hasItems = items.length > 0;
      const hasClosing = block.closing != null && String(block.closing).trim();
      if (!hasItems && !hasClosing) return null;
      const cardGridTitle = CARD_GRID_SECTION_TITLE[locale] || CARD_GRID_SECTION_TITLE[LOCALES.EN];
      return (
        <ContentSection key={index}>
          <InfoTitle as={motion.h3} variants={staggerItem}>{cardGridTitle}</InfoTitle>
          {hasItems && (
            <CardGrid>
              {items.map((item, i) => (
                <motion.div key={i} variants={staggerItem} style={{ minHeight: 200 }}>
                  <PreloadZone preload={() => preloadImagesForSolutionsPath(item.linkTo, solutions)}>
                    <SectionPageCard
                      as={LocaleLink}
                      to={item.linkTo}
                      $hasImage={Boolean(item.image)}
                    >
                      {item.image && (
                        <ImageClipWrapper>
                          <SectionPageCardImage
                            $hasImage={Boolean(item.image)}
                            $image={item.image}
                          />
                        </ImageClipWrapper>
                      )}
                      <IndustryCardContent>
                        <SectionPageCardTitle>{item.title}</SectionPageCardTitle>
                        <SectionPageCardDescription>{item.description}</SectionPageCardDescription>
                      </IndustryCardContent>
                      <CardArrowButton><CardArrowIcon /></CardArrowButton>
                    </SectionPageCard>
                  </PreloadZone>
                </motion.div>
                ))}
            </CardGrid>
          )}
          {hasClosing && (
            <ContentText as={motion.div} variants={staggerItem}>
              <p>{block.closing}</p>
            </ContentText>
          )}
        </ContentSection>
      );
    }
    if (block.type === 'button') {
      if (block.buttonLink == null || block.buttonText == null) return null;
      return (
        <ContentSection key={index}>
          <InlineButtonWrapper as={motion.div} variants={staggerItem}>
            <InlineButton to={block.buttonLink}>{block.buttonText}</InlineButton>
          </InlineButtonWrapper>
        </ContentSection>
      );
    }
    if (block.type === 'cta') {
      const hasCta = block.title != null || block.text != null || (block.buttonLink != null && block.buttonText != null);
      if (!hasCta) return null;
      const imageSrc = block.backgroundImage || section.heroImage || '/ui/handshake-loop.webp';
      const bgPos = block.backgroundPosition != null && String(block.backgroundPosition).trim()
        ? String(block.backgroundPosition).trim()
        : 'center';
      return (
        <CtaSection key={index}>
          <Container>
            <CtaImageSection src={imageSrc} overlay={0.45} backgroundPosition={bgPos}>
              <PartnershipSection
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
              <PartnershipContent>
                {block.title != null && String(block.title).trim() && (
                  <SolutionsCtaTitle as={motion.h3} variants={staggerItem}>{block.title}</SolutionsCtaTitle>
                )}
                {block.text != null && String(block.text).trim() && (
                  <SolutionsCtaText as={motion.p} variants={staggerItem}>{block.text}</SolutionsCtaText>
                )}
                {block.buttonLink != null && block.buttonText != null && (
                  <SolutionsCtaButton to={block.buttonLink}>{block.buttonText}</SolutionsCtaButton>
                )}
              </PartnershipContent>
            </PartnershipSection>
            </CtaImageSection>
          </Container>
        </CtaSection>
      );
    }
    return null;
  };

  // CTA blocks are rendered outside the main container (full-width partnership style)
  const contentBlocks = blocks.filter((b) => b.type !== 'cta');
  const ctaBlocks = blocks.filter((b) => b.type === 'cta');
  const renderableContentBlocks = contentBlocks
    .map((block, index) => ({ index, block, node: renderBlock(block, index) }))
    .filter(({ node }) => node != null);

  return (
    <>
      <SEO
        title={`${section.title} — APEXGATE GLOBAL`}
        description={section.subtitle || section.description || ''}
        image={section.heroImage}
      />
      <LazyBackgroundImage
        src={section.heroImage}
        critical={true}
        rounded={false}
        borderRadius={0}
        backgroundPosition="50% 70%"
      >
        <SolutionsHeroSection>
          <Container>
            <HeroContentWrapper>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={heroStaggerContainerVariants}
              >
                <HeroContent style={{ animation: 'none' }}>
                  <HeroTitle as={motion.h1} variants={heroStaggerItemVariants}>
                    {section.title}
                  </HeroTitle>
                </HeroContent>
              </motion.div>

              {/* Subsections grid: only show when no rich section content */}
              {subsections.length > 0 && !hasSectionContent && (
                <SubsectionsGrid>
                  {subsections.map(([subsectionId, subsection]) => {
                    const subUrls = getSubsectionImageUrls(subsection, section);
                    const onSubsectionPreload = () => {
                      if (subUrls.length) preloadCriticalImages(subUrls);
                    };
                    return (
                      <PreloadZone key={subsectionId} preload={onSubsectionPreload} inline>
                        <SubsectionLink
                          as={LocaleLink}
                          to={`/solutions/${sectionId}/${subsectionId}`}
                        >
                          {subsection.title}
                          <ViewLabelHero><CardArrowIcon /></ViewLabelHero>
                        </SubsectionLink>
                      </PreloadZone>
                    );
                  })}
                </SubsectionsGrid>
              )}
            </HeroContentWrapper>
          </Container>
        </SolutionsHeroSection>
      </LazyBackgroundImage>

      {/* Rich section content: format-driven blocks */}
      {hasSectionContent && (
        <PageContainer style={{ paddingTop: '60px' }}>
          <Container>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {renderableContentBlocks.map(({ index, block, node }, renderedIndex) => {
                const next = renderableContentBlocks[renderedIndex + 1];
                const shouldInsertAutoDivider =
                  renderedIndex < renderableContentBlocks.length - 1 &&
                  block?.type !== 'divider' &&
                  next?.block?.type !== 'divider';
                return (
                <Fragment key={`content-block-${index}`}>
                  {node}
                  {shouldInsertAutoDivider && <SectionDivider />}
                </Fragment>
              );})}
            </motion.div>
          </Container>
          {ctaBlocks.map((block, index) => renderBlock(block, index))}
        </PageContainer>
      )}
    </>
  );
};

export default SectionPage;

